<template>
    <template v-if="!isModal">
        <master-layout :title="t('login.title')" :back-button="true">
            <ion-card>
                <ion-card-content>
                    <form mode="md" @submit.prevent="submitForm">
                        <button type="submit" v-show="false"></button>
                        <ion-item class="mb">
                            <ion-label position="floating">{{ t('login.username') }}</ion-label>
                            <ion-input v-model="credentials.username" inputmode="email"></ion-input>
                            <ion-note v-if="v$.username.$errors.length">{{ t('login.username_required') }}</ion-note>
                        </ion-item>
                        <ion-item class="mb">
                            <ion-label position="floating">{{ t('login.password') }}</ion-label>
                            <ion-input v-model="credentials.password" type="password"></ion-input>
                            <ion-note v-if="v$.password.$errors.length">{{ t('login.password_required') }}</ion-note>
                        </ion-item>
                        <div class="ion-text-end mb">
                            <ion-button fill="clear" @click="forgotPassword">
                                {{ t('login.forgot_password') }}
                            </ion-button>
                        </div>
                    </form>
                    <ion-button fill="outline" expand="block" @click="requestAccess">
                        {{ t('login.request_access') }}
                    </ion-button>
                </ion-card-content>
            </ion-card>
            <WideFab @click="submitForm" :loading="loading">{{ t('login.login') }}</WideFab>
        </master-layout>
    </template>
    <template v-else>
        <ion-card-content>
            <form mode="md" @submit.prevent="submitForm">
                <button type="submit" v-show="false"></button>
                <ion-item class="mb">
                    <ion-label position="floating">{{ t('login.username') }}</ion-label>
                    <ion-input v-model="credentials.username" inputmode="email"></ion-input>
                    <ion-note v-if="v$.username.$errors.length">{{ t('login.username_required') }}</ion-note>
                </ion-item>
                <ion-item class="mb">
                    <ion-label position="floating">{{ t('login.password') }}</ion-label>
                    <ion-input v-model="credentials.password" type="password"></ion-input>
                    <ion-note v-if="v$.password.$errors.length">{{ t('login.password_required') }}</ion-note>
                </ion-item>
                <div class="ion-text-end mb">
                    <ion-button fill="clear" @click="forgotPassword">
                        {{ t('login.forgot_password') }}
                    </ion-button>
                </div>
            </form>
            <ion-button @click="submitForm" :disabled="loading" expand="block" class="mb">
                <ion-spinner v-if="loading" name="dots"></ion-spinner>
                <template v-else>{{ t('login.login') }}</template>
            </ion-button>
            <ion-button fill="outline" expand="block" @click="requestAccess">
                {{ t('login.request_access') }}
            </ion-button>
        </ion-card-content>
    </template>
</template>

<script>
import { IonCard, IonCardContent, IonItem, IonLabel, IonInput, IonNote, IonButton, IonSpinner, toastController, modalController } from '@ionic/vue';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { login } from '@/http/auth';
import { isOnline } from '@/helpers/network';
import store from '@/store';
import WideFab from '@/components/WideFab';

export default {
    name: 'LoginPage',
    components: { IonCard, IonCardContent, IonItem, IonLabel, IonInput, IonNote, IonButton, IonSpinner, WideFab },
    props: ['isModal'],
    setup(props) {
        const router = useRouter();
        const { t } = useI18n();

        const emptyCredentials = {
            username: '',
            password: ''
        };

        const credentials = ref({ ...emptyCredentials });

        // Postavi pravila validacije (korisničko ime i lozinka su obavezni).
        const v$ = useVuelidate({
            username: { required },
            password: { required }
        }, credentials);

        const forgotPassword = () => {
            if(props.isModal) {
                modalController.dismiss();
            }

            router.push({ name: 'forgot-password' });
        }

        const requestAccess = () => {
            if(props.isModal) {
                modalController.dismiss();
            }

            router.push({ name: 'request-access' });
        }

        const loading = ref(false);

        // Pošalji podatke serveru.
        const submitForm = async () => {
            if(!await v$.value.$validate()) return;

            loading.value = true;

            if(!await isOnline()) {
                loading.value = false;
                return toastController.create({ message: t('login.no_connection'), duration: 2000 }).then(t => t.present());
            }

            const response = await login(credentials.value);

            // Prikaži poruku sa servera ako je prijava neuspješna.
            if(!response.status) {
                loading.value = false;
                return toastController.create({ message: response.message, duration: 2000 }).then(t => t.present());
            }

            // Spremi token.
            await store.dispatch('auth/login', response);

            loading.value = false;

            if(store.getters['auth/isLoggedIn']) {
                // Sakrij modal ako je pristuan.
                if(props.isModal) {
                    modalController.dismiss();
                } else {
                    router.push({ name: 'competitions' });
                }

                // Resetiraj inpute i validaciju inputa.
                credentials.value = { ...emptyCredentials };
                v$.value.$reset();
            } else {
                toastController.create({ message: t('login.error'), duration: 2000 }).then(t => t.present());
            }
        };

        return {
            credentials,
            forgotPassword,
            requestAccess,
            submitForm,
            loading,
            v$,
            t
        };
    }
}
</script>

<style lang="scss" scoped>
@import '@/theme/forms.scss';

ion-content {
    --padding-bottom: 52px;
}

.mb {
    margin-bottom: 16px;
}

.ion-text-end ion-button {
    text-transform: none;
}
</style>